import { HttpStatusCode } from 'axios'

import AccessTokenStore from '../accessTokenStore'
import AuthClient from '../authClient'
import reportAuthFailure from '../reportAuthFailure'

const GUEST_SESSION_PATH = 'api/v1/guest/session'

// Longer than the server allows itself to create a guest, which is 20 seconds
// at most. A browser that gave up sooner would leave behind a guest the server
// finished creating but whose cookies never arrived, and nothing ever removes
// that guest.
const REQUEST_TIMEOUT = 25 * 1_000

const ALREADY_AUTHENTICATED = Object.freeze({
  isAlreadyAuthenticated: true,
  isTokenStored: false,
})

const STILL_ANONYMOUS = Object.freeze({
  isAlreadyAuthenticated: false,
  isTokenStored: false,
})


// Gives an anonymous visitor a guest identity and stores the access token that
// comes with it, which authenticates the guest's requests like any other.
//
// A token that arrives after the store was cleared has been overtaken by a
// sign-out, so it is dropped rather than put back into memory.
//
// The endpoint answers a 204 that sets no cookies while the server is not
// handing out guest identities, and the visitor stays anonymous.
//
// Resolves to an object with the shape { isAlreadyAuthenticated, isTokenStored }.
// A browser that is already signed in gets a 409 that sets no cookies.
//
export default async function bootstrapGuestSession() {
  const generation = AccessTokenStore.getGeneration()

  try {
    const { data, status } = await AuthClient.post(GUEST_SESSION_PATH, void 0, {
      timeout: REQUEST_TIMEOUT,
    })

    if ( status === HttpStatusCode.NoContent ) {
      return STILL_ANONYMOUS
    }

    const isStillCurrent = ( AccessTokenStore.getGeneration() === generation )

    if ( isStillCurrent ) {
      AccessTokenStore.setAccessToken({
        accessToken: data.access_token,
        expiresIn: data.expires_in,
      })
    }

    return {
      isAlreadyAuthenticated: false,
      isTokenStored: isStillCurrent,
    }
  } catch (error) {
    if ( error?.response?.status === HttpStatusCode.Conflict ) {
      return ALREADY_AUTHENTICATED
    }

    // Every other failure leaves the visitor anonymous, which is where they
    // started. Reported so that a broken identity service stays visible, but
    // not retried: the endpoint is throttled, so retrying would spend the
    // allowance that the next visitor needs to get an identity at all.
    reportAuthFailure(error)

    return STILL_ANONYMOUS
  }
}
